import { type TeamsSchema } from "@/hooks/roster-craete/useTeamsQuery";
import { ManagerTable } from "./ManagerTable";
import { useMemo } from "react";
import useNurses from "@/hooks/useNurses";
import { linkPreceptorRelations } from "@/utils/nurse-util";
import { images } from "@/assets/img/imagesMap";
import { useSortableNurses } from "@/hooks/useSortableNurses";
import { compareNurses } from "@/utils/sort-util";
import {
  nurseRoleSortIndex,
  sortNurseListByRoleThenSequence,
} from "@/utils/common-util";
import { sortTeamsByName } from "@/utils/team-sort-util";

export default function NurseManagementTables({
  localTeams,
  currentTeamId: _currentTeamId,
  selectedGroupId,
  activeNurseIdForSideProfile,
  selectIdForSideprofile,
  showOnlyActive = true, // 기본값: 활성 인원만 표시
  isAddNewNurse = false,
  setIsAddNewNurse,
  viewState = "ALL",
  sortConfig: externalSortConfig,
  onSort: externalOnSort,
}: {
  localTeams: TeamsSchema;
  setLocalTeams: React.Dispatch<React.SetStateAction<TeamsSchema>>;
  currentTeamId: number | null;
  setCurrentTeamId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedGroupId: string | null;
  activeNurseIdForSideProfile: string | null;
  selectIdForSideprofile: (bool: boolean, nurseID: string) => void;
  showOnlyActive?: boolean; // 활성(true) 또는 비활성(false) 인원만 표시
  isAddNewNurse?: boolean;
  setIsAddNewNurse?: React.Dispatch<React.SetStateAction<boolean>>;
  viewState?: "ALL" | "TEAM";
  sortConfig?: {
    key: "experience" | "grade" | "name" | null;
    order: "asc" | "desc" | null;
  };
  onSort?: (key: "experience" | "grade" | "name") => void;
}) {
  void _currentTeamId;
  const { data } = useNurses(selectedGroupId);
  const linkedAndRoleSortedNurses = useMemo(
    () => linkPreceptorRelations(sortNurseListByRoleThenSequence(data ?? [])),
    [data]
  );

  // 외부에서 정렬 상태를 받으면 사용, 없으면 내부에서 생성
  const {
    sortedNurses: internalSortedNurses,
    sortConfig: internalSortConfig,
    handleSort: internalHandleSort,
  } = useSortableNurses(linkedAndRoleSortedNurses, {
    enableSequenceUpdate: showOnlyActive !== false, // 비활성 근무자는 false
    keepRoleGroupOrder: true,
  });

  // 외부 정렬 상태가 있으면 외부 것을 사용, 없으면 내부 것 사용
  const sortConfig = externalSortConfig ?? internalSortConfig;
  const handleSort = externalOnSort ?? internalHandleSort;

  // 외부 정렬 상태가 있으면 외부 정렬을 직접 적용, 없으면 내부 정렬 사용
  const sortedNurses = useMemo(() => {
    const baseNurses = linkedAndRoleSortedNurses;

    // 외부 정렬 상태가 있으면 외부 정렬 적용
    if (externalSortConfig?.key && externalSortConfig?.order) {
      const sorter = (a: (typeof baseNurses)[0], b: (typeof baseNurses)[0]) => {
        const roleDiff =
          nurseRoleSortIndex(a.role) - nurseRoleSortIndex(b.role);
        if (roleDiff !== 0) return roleDiff;
        return compareNurses(
          a,
          b,
          externalSortConfig.key!,
          externalSortConfig.order as "asc" | "desc"
        );
      };
      return [...baseNurses].sort(sorter);
    }

    // 외부 정렬 상태가 없으면 내부 정렬 사용
    return internalSortedNurses;
  }, [linkedAndRoleSortedNurses, externalSortConfig, internalSortedNurses]);

  // 전체 간호사 (showOnlyActive에 따라 활성 또는 비활성만)
  const allNursesWithRelations = useMemo(() => {
    return showOnlyActive
      ? sortedNurses.filter((n) => n.active === 1)
      : sortedNurses.filter((n) => n.active === 0);
  }, [sortedNurses, showOnlyActive]);

  // 미지정 인원
  const unassignedNurses = useMemo(() => {
    return sortedNurses.filter((n) => !n.team_id);
  }, [sortedNurses]);

  // 팀명 기준으로 정렬된 팀 리스트
  const sortedTeams = useMemo(() => {
    if (!localTeams) return [];

    return sortTeamsByName(localTeams);
  }, [localTeams]);

  if (viewState === "TEAM") {
    // 비활성 근무자 섹션에서는 팀별 보기를 하지 않음 (전체보기로 표시)
    if (!showOnlyActive) {
      return (
        <div className="flex flex-col">
          <ManagerTable
            teamName="전체"
            selectedGroupId={selectedGroupId ?? null}
            nurseDataWithRelation={allNursesWithRelations}
            activeNurseIdForSideProfile={activeNurseIdForSideProfile ?? null}
            selectIdForSideprofile={selectIdForSideprofile}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-8">
        {/* 팀별 리스트 */}
        {sortedTeams.map((team) => {
          // 해당 팀의 활성 인원만 필터링
          const teamMembers = sortedNurses.filter(
            (n) => n.team_id === team.team_id && n.active === 1
          );

          return (
            <div key={team.team_id} className="flex flex-col">
              <h3 className="px-10 text-lg font-bold text-white bg-sig p-2 text-center rounded-t-lg grid grid-cols-3">
                <div className="flex items-center gap-2">
                  <img src={images.icon_person} alt="" className="size-3 " />
                  {teamMembers.length}
                </div>
                <div>{team.team_name} 팀</div>
                <div></div>
              </h3>
              <ManagerTable
                teamName={team.team_name}
                selectedGroupId={selectedGroupId ?? null}
                nurseDataWithRelation={teamMembers}
                activeNurseIdForSideProfile={
                  activeNurseIdForSideProfile ?? null
                }
                selectIdForSideprofile={selectIdForSideprofile}
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>
          );
        })}

        {/* 미지정 리스트 */}
        <div className="flex flex-col">
          <h3 className="px-10 text-lg font-bold text-white bg-[#C4C4C4] p-2 text-center rounded-t-lg grid grid-cols-3">
            <div className="flex items-center gap-2">
              <img src={images.icon_person} alt="" className="size-3 " />
              {unassignedNurses.length}
            </div>
            <div>미지정</div>
            <div></div>
          </h3>
          <ManagerTable
            teamName="미지정"
            selectedGroupId={selectedGroupId ?? null}
            nurseDataWithRelation={unassignedNurses}
            activeNurseIdForSideProfile={activeNurseIdForSideProfile ?? null}
            selectIdForSideprofile={selectIdForSideprofile}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>
      </div>
    );
  }

  // ALL view (전체보기 모드)
  return (
    <div className="flex flex-col">
      <ManagerTable
        teamName="전체"
        selectedGroupId={selectedGroupId ?? null}
        nurseDataWithRelation={allNursesWithRelations}
        activeNurseIdForSideProfile={activeNurseIdForSideProfile ?? null}
        selectIdForSideprofile={selectIdForSideprofile}
        sortConfig={sortConfig}
        onSort={handleSort}
        isAddNewNurse={isAddNewNurse}
        setIsAddNewNurse={setIsAddNewNurse}
      />
    </div>
  );
}
